import { useMemo } from "react";

import api from "/imports/api/client";
import { useAuth } from "/imports/state/AuthContext";
import { useWidget } from "/imports/state/WidgetContext";
import useFetch from "./useFetch";

/**
 * The wallets whose balance covers `price` in full — equal counts, a cent short does not.
 */
export function walletsCoveringPrice(items, price) {
  const amount = Number(price) || 0;
  if (!(amount > 0)) return [];
  return (items || []).filter((w) => (parseFloat(w.remainValue) || 0) >= amount);
}

/**
 * Wallets that can pay for a package outright. Only a wallet whose balance covers
 * the WHOLE price is offered: a part-wallet purchase would still send the client
 * to the card page, and an abandoned card payment does not give the wallet back.
 * The server refuses a short wallet too, so this list is the courtesy, not the rule.
 *
 * Fetched each time the confirm opens, since a balance can move between purchases.
 * The location falls back to the club's first one, as the server's does.
 */
export default function usePackWallets({ open, locationId, price }) {
  const { slug, locations } = useWidget();
  const { isAuthenticated, activeClientId } = useAuth();
  const walletLocationId = locationId || locations?.[0]?._id || null;
  const amount = Number(price) || 0;

  const { data } = useFetch(
    () => api.getRedeemableVouchers(slug, { locationId: walletLocationId, forSale: true }),
    [slug, walletLocationId, activeClientId, open],
    { skip: !open || !slug || !walletLocationId || !isAuthenticated || !(amount > 0) }
  );

  return useMemo(() => {
    // useFetch keeps its last answer while skipped, so a closed (or non-package)
    // confirm must not inherit the wallets fetched for the previous one.
    if (!open) return [];
    return walletsCoveringPrice(data?.items, amount);
  }, [data, amount, open]);
}
